import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Phone,
  MessageSquare,
  Shield,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ArrowRightLeft,
  FileText,
  Users,
  Smartphone,
  Landmark,
  HelpCircle,
  Zap,
  ExternalLink,
} from "lucide-react";

// ─── Data ────────────────────────────────────────────────────────────────────

const BENEFITS = [
  {
    icon: Phone,
    title: "Keep Your Number",
    description: "Clients already know your number. Keep it and manage everything from Kavoa.",
    color: "bg-cyan-50 text-cyan-600",
  },
  {
    icon: MessageSquare,
    title: "Enable SMS & Texting",
    description: "Even landline numbers gain full SMS capability once ported. Text clients from your office number.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Zap,
    title: "One Platform",
    description: "Calls, texts, and emails all in one place. No switching between apps or devices.",
    color: "bg-violet-50 text-violet-600",
  },
];

const PREPARATION_ITEMS = [
  "Your account number with your current carrier",
  "Your account PIN or password",
  "The exact service address on file with your carrier",
  "The authorized account holder's name (must match exactly)",
  "A recent phone bill or invoice (recommended)",
  "Equipment Record from Bell or Telus (if applicable)",
];

const STEPS = [
  {
    num: 1,
    title: "Call Your Current Carrier",
    icon: Phone,
    content: `Contact your current phone provider and request the following information. It's important that every detail matches exactly what they have on file. Even small differences (like "St." vs "Street") can cause the port to be rejected.

**Ask for:**
- Your full account number
- Your account PIN or password
- The exact service address on file (not billing address)
- The authorized name on the account
- Whether you're under contract and any early termination fees
- Whether there are any pending service orders (these must be cancelled first)
- If you're with Bell or Telus, request an "Equipment Record"

**Important:** Do NOT cancel your current service. Your number stays active with your old carrier until the port is complete. Cancelling early can mean losing your number permanently.`,
  },
  {
    num: 2,
    title: "Gather Your Documents",
    icon: FileText,
    content: `You'll need to sign a Letter of Authorization (LOA), a standard industry document that authorizes the transfer of your number. We provide the LOA template.

**Required documents:**
- Signed Letter of Authorization (we provide it, you sign)
- Account number and PIN
- Service address (must match carrier records exactly)
- Authorized account holder's full name
- Recent phone bill (helps resolve disputes if the carrier pushes back)

**For businesses:** Use the exact business name that appears on the account, not a shortened version or DBA name.`,
  },
  {
    num: 3,
    title: "Submit Your Port Request",
    icon: ArrowRightLeft,
    content: `Head to **Settings → Phone Numbers** and click "Port a Number." Enter your phone number, current carrier, and we'll submit the request to begin the transfer.

Once submitted:
1. We validate your information (up to 24 hours)
2. The request is sent to your carrier's porting team
3. Your carrier reviews and either accepts or rejects the request
4. If accepted, they issue a Firm Order Commitment (FOC) date, which is when your number will transfer`,
  },
  {
    num: 4,
    title: "Approve the Transfer",
    icon: Smartphone,
    content: `**For mobile numbers only:** Your current carrier will send an SMS to the number being ported asking you to confirm the transfer.

**You have exactly 90 minutes to reply and approve.** If you miss this window, the port request is rejected and we'll need to resubmit, which adds 1-2 weeks to the timeline.

**Tips:**
- Keep the phone powered on and nearby after submitting
- Make sure SMS is working on the device
- Set a reminder to check for the confirmation text
- The message will come from your current carrier, not from Kavoa

**For landline numbers:** There is no SMS approval step. The process goes through a Firm Order Commitment (FOC) workflow between carriers instead.`,
  },
  {
    num: 5,
    title: "Confirmation & Go Live",
    icon: CheckCircle2,
    content: `Once the port completes, your number is live on Kavoa. You can immediately:

- **Send and receive SMS**, even if it was a landline before
- **Make and receive calls** through the Kavoa softphone
- **Use the number** for all client communications

**After confirmation:**
- You can now cancel service with your old carrier (the number has been transferred)
- Your old carrier will send a final bill
- Any device balance or early termination fees will appear on that final bill

The number retains its original area code and geographic identity. Clients calling it won't notice any change.`,
  },
];

interface CarrierInfo {
  name: string;
  parent?: string;
  type: string;
  notes: string[];
  askFor: string[];
}

const CARRIERS: CarrierInfo[] = [
  {
    name: "Bell",
    type: "Major (Wireless + Wireline)",
    notes: [
      "Request an Equipment Record, which documents all services on your account",
      "If you have multiple lines, provide the Billing Telephone Number (BTN)",
      "Bell's porting team can take 5-7 business days to process",
    ],
    askFor: ["Account number", "PIN", "Equipment Record", "BTN (if multi-line)", "Service address"],
  },
  {
    name: "Rogers",
    type: "Major (Wireless)",
    notes: [
      "PIN is mandatory. You cannot port without it",
      "For mobile ports, watch for the 90-minute SMS approval",
      "Rogers processes ports relatively quickly among Canadian carriers",
    ],
    askFor: ["Account number", "PIN (mandatory)", "Service address", "Authorized name"],
  },
  {
    name: "Telus",
    type: "Major (Wireless + Wireline)",
    notes: [
      "Request an Equipment Record. Telus provides these on request",
      "Koodo and Public Mobile use the same backend systems",
      "Telus landline ports may take up to 14 business days",
    ],
    askFor: ["Account number", "PIN", "Equipment Record", "Service address"],
  },
  {
    name: "Fido",
    parent: "Rogers",
    type: "Wireless (Rogers subsidiary)",
    notes: [
      "Same porting process as Rogers",
      "PIN is mandatory",
      "90-minute SMS confirmation for mobile numbers",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
  {
    name: "Koodo",
    parent: "Telus",
    type: "Wireless (Telus subsidiary)",
    notes: [
      "Same porting process as Telus",
      "Online self-serve may show a different account number, so call to confirm",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
  {
    name: "Virgin Plus",
    parent: "Bell",
    type: "Wireless (Bell subsidiary)",
    notes: [
      "Same porting process as Bell",
      "Equipment Record may not be available for Virgin-only accounts",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
  {
    name: "Freedom Mobile",
    type: "Wireless",
    notes: [
      "Standard porting process",
      "Some older Wind Mobile numbers may require additional verification",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
  {
    name: "SaskTel",
    type: "Regional (Wireless + Wireline)",
    notes: [
      "Most SaskTel numbers can be ported",
      "Some numbers in very small or remote communities may not be portable if no VoIP carrier has presence in that rate centre",
      "Contact us first if you're in a small town so we can verify portability",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
  {
    name: "Videotron",
    type: "Regional (Quebec)",
    notes: [
      "Quebec regional carrier with a standard porting process",
      "French-language support available from their porting team",
    ],
    askFor: ["Account number", "PIN", "Service address"],
  },
];

const FAQ = [
  {
    q: "Can I port a landline number?",
    a: "Yes! And here's the best part: once ported to Kavoa, your landline number gains full SMS texting capability. You'll be able to send and receive text messages from your office number, which is not possible with a traditional landline. This is one of the biggest benefits of porting.",
  },
  {
    q: "How long does the porting process take?",
    a: "Mobile numbers typically take 5-7 business days. Landline numbers take 7-14 business days. If the port request is rejected (usually due to a typo in account details), add 1-2 weeks per rejection cycle. We'll keep you updated throughout the process.",
  },
  {
    q: "Will I lose phone service during the port?",
    a: "No. Your number continues to work normally with your current carrier until the exact moment the port completes. There may be a brief interruption (a few minutes) during the switchover. After that, all calls and texts route through Kavoa.",
  },
  {
    q: "What if my port request is rejected?",
    a: "The most common reason is a mismatch in account details. If the name, address, or PIN doesn't match exactly what the carrier has on file, the port will be rejected. We'll tell you the rejection reason, you fix the discrepancy, and we resubmit. Other common issues: pending service orders on the account, or missing the 90-minute SMS approval window for mobile ports.",
  },
  {
    q: "Does porting work with all Canadian carriers?",
    a: "Yes. Under CRTC regulations, every Canadian carrier is legally required to release phone numbers when a valid port request is submitted. This applies to all major carriers (Bell, Rogers, Telus) and all smaller carriers (Fido, Koodo, Virgin Plus, Freedom, SaskTel, Videotron, etc.). The only rare exception is numbers in extremely remote communities where no VoIP carrier has rate-centre coverage.",
  },
  {
    q: "What does it cost?",
    a: "Your current carrier cannot charge you a fee for porting out (CRTC rules). However, there may be early termination fees if you're under contract, and any device balance becomes due. On the Kavoa side, ported numbers have a small monthly fee (approximately $1.15 USD/month) plus standard usage charges for calls and texts.",
  },
  {
    q: "Can I port my number back if I change my mind?",
    a: "Yes. Number porting is fully reversible. You can port your number to any other carrier at any time with no lock-in periods.",
  },
  {
    q: "Do I need to cancel my old service?",
    a: "Do NOT cancel before the port completes. This is critical. If you cancel early, you risk losing your number permanently. Once we confirm the port is complete, then you can cancel. Your old carrier will send you a final bill.",
  },
  {
    q: "What about my firm's other phone lines?",
    a: "You can port multiple numbers. Each number is ported individually. If you have a multi-line account, make sure to provide the Billing Telephone Number (BTN) and specify which lines you want to port.",
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const PortingGuidePage: React.FC = () => {
  const navigate = useNavigate();
  const [expandedStep, setExpandedStep] = useState<number | null>(1);
  const [expandedCarrier, setExpandedCarrier] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const toggleStep = (num: number) => setExpandedStep(expandedStep === num ? null : num);
  const toggleCarrier = (name: string) => setExpandedCarrier(expandedCarrier === name ? null : name);
  const toggleFaq = (idx: number) => setExpandedFaq(expandedFaq === idx ? null : idx);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-cyan-50 mb-4">
          <ArrowRightLeft className="h-7 w-7 text-cyan-600" />
        </div>
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Port Your Number to Kavoa</h1>
        <p className="text-base text-slate-500 max-w-xl mx-auto leading-relaxed">
          Transfer your existing phone number to Kavoa and unlock SMS texting, call management,
          and unified communications, all from one platform.
        </p>
      </motion.div>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        {BENEFITS.map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
          >
            <div className={`inline-flex items-center justify-center h-10 w-10 rounded-xl ${b.color} mb-3`}>
              <b.icon className="h-5 w-5" />
            </div>
            <h3 className="font-semibold text-slate-800 mb-1">{b.title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{b.description}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Landline callout ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-10"
      >
        <Landmark className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">Have a landline number?</p>
          <p className="text-sm text-amber-700 mt-0.5 leading-relaxed">
            Traditional landlines can't send or receive text messages. When you port your landline to Kavoa,
            it automatically gains full SMS capability. Your clients can text your office number directly.
            No extra setup required.
          </p>
        </div>
      </motion.div>

      {/* ── What You'll Need ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">What You'll Need</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Gather this information from your current carrier before starting. Having everything ready
          helps avoid rejections and delays.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {PREPARATION_ITEMS.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-slate-700">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Step-by-Step ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="mb-10"
      >
        <h2 className="text-lg font-bold text-slate-800 mb-4">How It Works</h2>
        <div className="space-y-2">
          {STEPS.map((step) => (
            <div
              key={step.num}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleStep(step.num)}
                className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {step.num}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800">{step.title}</span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform ${
                    expandedStep === step.num ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {expandedStep === step.num && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pl-16">
                      <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line prose prose-sm prose-slate max-w-none">
                        {step.content.split("\n").map((line, i) => {
                          const trimmed = line.trim();
                          if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
                            return (
                              <p key={i} className="font-semibold text-slate-800 mt-3 mb-1">
                                {trimmed.replace(/\*\*/g, "")}
                              </p>
                            );
                          }
                          if (trimmed.startsWith("- ")) {
                            return (
                              <div key={i} className="flex items-start gap-2 ml-1 my-0.5">
                                <span className="text-slate-400 mt-1">•</span>
                                <span>{renderBold(trimmed.slice(2))}</span>
                              </div>
                            );
                          }
                          if (trimmed.match(/^\d+\./)) {
                            return (
                              <div key={i} className="flex items-start gap-2 ml-1 my-0.5">
                                <span className="text-slate-400 font-medium">{trimmed.split(".")[0]}.</span>
                                <span>{renderBold(trimmed.slice(trimmed.indexOf(".") + 2))}</span>
                              </div>
                            );
                          }
                          if (!trimmed) return <div key={i} className="h-2" />;
                          return <p key={i} className="my-1">{renderBold(trimmed)}</p>;
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Timeline ──────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm mb-10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-cyan-50 flex items-center justify-center">
            <Clock className="h-4 w-4 text-cyan-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Expected Timeline</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <Smartphone className="h-5 w-5 text-slate-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">5-7</p>
            <p className="text-xs text-slate-500 mt-0.5">business days (mobile)</p>
          </div>
          <div className="p-4 bg-slate-50 rounded-xl text-center">
            <Landmark className="h-5 w-5 text-slate-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-slate-800">7-14</p>
            <p className="text-xs text-slate-500 mt-0.5">business days (landline)</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-xl text-center">
            <AlertTriangle className="h-5 w-5 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-700">+1-2 wk</p>
            <p className="text-xs text-amber-600 mt-0.5">if rejected (per cycle)</p>
          </div>
        </div>
      </motion.div>

      {/* ── Carrier Guide ─────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-cyan-50 flex items-center justify-center">
            <Users className="h-4 w-4 text-cyan-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Carrier-Specific Guide</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          All Canadian carriers are legally required to release your number (CRTC regulation). Here's what to expect from each.
        </p>
        <div className="space-y-2">
          {CARRIERS.map((carrier) => (
            <div
              key={carrier.name}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleCarrier(carrier.name)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-slate-800">{carrier.name}</span>
                  {carrier.parent && (
                    <span className="ml-2 text-xs text-slate-400">({carrier.parent} subsidiary)</span>
                  )}
                  <span className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500">
                    {carrier.type}
                  </span>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">
                  Supported
                </span>
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 transition-transform ${
                    expandedCarrier === carrier.name ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {expandedCarrier === carrier.name && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                          What to ask for
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {carrier.askFor.map((item) => (
                            <span
                              key={item}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium bg-cyan-50 text-cyan-700"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                          Notes
                        </p>
                        {carrier.notes.map((note, i) => (
                          <div key={i} className="flex items-start gap-2 my-1">
                            <span className="text-slate-400 mt-0.5 text-xs">•</span>
                            <span className="text-sm text-slate-600">{note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Common Rejection Reasons ──────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-red-50 border border-red-200 rounded-xl p-6 mb-10"
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <h2 className="text-lg font-bold text-red-800">Common Rejection Reasons</h2>
        </div>
        <p className="text-sm text-red-700 mb-3">
          Avoid these common mistakes to ensure a smooth port:
        </p>
        <div className="space-y-2">
          {[
            ["Account info mismatch", "Name, address, or PIN doesn't match carrier records exactly"],
            ["Expired or unsigned LOA", "The Letter of Authorization must be dated within the last 30 days"],
            ["Missed 90-minute approval", "For mobile ports, reply to the carrier's SMS within 90 minutes"],
            ["Pending service orders", "Cancel any pending plan changes before submitting the port"],
            ["Service already cancelled", "Never cancel your old service before the port is confirmed"],
            ["Outstanding balance", "Some carriers block port-outs with unpaid balances"],
          ].map(([title, desc]) => (
            <div key={title} className="flex items-start gap-2">
              <span className="text-red-400 mt-0.5">•</span>
              <div>
                <span className="text-sm font-medium text-red-800">{title}</span>
                <span className="text-sm text-red-700">: {desc}</span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-lg bg-cyan-50 flex items-center justify-center">
            <HelpCircle className="h-4 w-4 text-cyan-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-800">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-2">
          {FAQ.map((item, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(idx)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="text-sm font-medium text-slate-800 flex-1">{item.q}</span>
                <ChevronDown
                  className={`h-4 w-4 text-slate-400 flex-shrink-0 transition-transform ${
                    expandedFaq === idx ? "rotate-180" : ""
                  }`}
                />
              </button>
              <AnimatePresence>
                {expandedFaq === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                      <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── CRTC Note ─────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl mb-10"
      >
        <Shield className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-slate-700">Your Right to Port</p>
          <p className="text-sm text-slate-500 mt-0.5 leading-relaxed">
            Under CRTC regulations, every Canadian carrier is legally required to release your phone number
            when you request a port. Carriers cannot refuse a valid port request or charge you a fee for
            porting out. This applies to all wireless and wireline providers nationwide.
          </p>
        </div>
      </motion.div>

      {/* ── CTA Footer ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        className="text-center bg-white rounded-xl border border-slate-200 p-8 shadow-sm"
      >
        <h2 className="text-xl font-bold text-slate-800 mb-2">Ready to Port Your Number?</h2>
        <p className="text-sm text-slate-500 mb-5 max-w-md mx-auto">
          Head to Phone Numbers to submit your port request. Make sure you've gathered all
          the information listed above first.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => navigate("/settings/phone-numbers?port=true")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-bold transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
          >
            <ArrowRightLeft className="h-4 w-4" />
            Port My Number
          </button>
          <button
            onClick={() => navigate("/communications")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Back to Communications
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function renderBold(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-slate-800">
        {part}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export default PortingGuidePage;
